import React, { useEffect, useMemo, useRef, useState } from "react";

import PropTypes from "prop-types";
import {
  Button,
  Input,
  message,
  Modal,
  Select,
  Switch,
  Typography,
  Upload,
} from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";

import {
  createPlatformBanner,
  updatePlatformBanner,
  uploadImage,
} from "../api";
import { getBase64, validImageExtensions } from "../../Utils/file";
import ConfirmationModal from "../../Components/ConfirmationModal/ConfirmationModal";

import {
  BANNER_IMAGE_HINT,
  getPlatformOptions,
  getSetupOptions,
  getTierOptions,
} from "./constants";

const { Option } = Select;

const COMBO_CONFLICT_MESSAGE =
  "Another banner already exists for this combination.";

const isValidUrl = (value) => {
  if (!value) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch (err) {
    return false;
  }
};

const buildInitialState = (banner) => ({
  platform: banner?.platform || undefined,
  platformSetup: banner?.platformSetup || undefined,
  tier: banner?.tier || undefined,
  bannerUrl: banner?.bannerUrl || "",
  redirectUrl: banner?.redirectUrl || "",
  isActive: banner?.isActive ?? false,
});

const BannerFormModal = ({
  open,
  mode, // 'create' | 'edit'
  banner, // existing banner row when editing
  banners, // full list — used to enforce unique combo on the client
  onClose,
  onSaved,
}) => {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(() => buildInitialState(banner));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(banner?.bannerUrl || "");
  const [submitting, setSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const initialRef = useRef(buildInitialState(banner));

  useEffect(() => {
    if (open) {
      const init = buildInitialState(banner);
      setForm(init);
      setImageFile(null);
      setImagePreview(banner?.bannerUrl || "");
      initialRef.current = init;
    }
  }, [open, banner]);

  const platformOptions = useMemo(() => getPlatformOptions(), []);
  const setupOptions = useMemo(
    () => getSetupOptions(form.platform),
    [form.platform]
  );
  const tierOptions = useMemo(
    () => getTierOptions(form.platform, form.platformSetup),
    [form.platform, form.platformSetup]
  );

  const isDirty = useMemo(() => {
    const init = initialRef.current;
    return (
      form.platform !== init.platform ||
      form.platformSetup !== init.platformSetup ||
      form.tier !== init.tier ||
      form.redirectUrl !== init.redirectUrl ||
      form.isActive !== init.isActive ||
      Boolean(imageFile)
    );
  }, [form, imageFile]);

  // Only one banner per (platform, platformSetup, tier) combo is allowed —
  // active or inactive. In edit mode we exclude the row being edited.
  // Evaluated only on submit so the user isn't nagged while typing.
  const checkComboConflict = () => {
    if (!form.platform || !form.platformSetup || !form.tier) return false;
    return (banners || []).some(
      (b) =>
        b.platform === form.platform &&
        b.platformSetup === form.platformSetup &&
        b.tier === form.tier &&
        (!isEdit || b._id !== banner?._id)
    );
  };

  // Domain change always wipes the cascading selections — even when the
  // current setup also exists under the new domain (e.g. WL→TagMango both
  // expose `freedom`/`enterprise`), the tier matrix differs, so it's safer
  // to force the user to re-pick rather than leave a stale tier in place.
  const handlePlatformChange = (value) => {
    setForm((prev) => ({
      ...prev,
      platform: value,
      platformSetup: undefined,
      tier: undefined,
    }));
  };

  // Setup change always resets the tier; if the new setup has exactly one
  // tier we auto-select it for convenience.
  const handleSetupChange = (value) => {
    setForm((prev) => {
      const allowedTiers = getTierOptions(prev.platform, value).map(
        (o) => o.value
      );
      return {
        ...prev,
        platformSetup: value,
        tier: allowedTiers.length === 1 ? allowedTiers[0] : undefined,
      };
    });
  };

  const handleTierChange = (value) => {
    setForm((prev) => ({ ...prev, tier: value }));
  };

  const handleBeforeUpload = (file) => {
    const isValidExt = validImageExtensions.indexOf(file.type) !== -1;
    if (!isValidExt) {
      message.error("Please upload a JPG, JPEG, PNG or GIF image.");
      return Upload.LIST_IGNORE;
    }
    const sizeKB = file.size / 1024;
    if (sizeKB > BANNER_IMAGE_HINT.maxSizeKB) {
      message.error(
        `Image must be smaller than ${BANNER_IMAGE_HINT.maxSizeLabel}.`
      );
      return Upload.LIST_IGNORE;
    }
    setImageFile(file);
    getBase64(file, (url) => setImagePreview(url));
    return false;
  };

  const validate = () => {
    if (!form.platform) {
      message.error("Please select a domain.");
      return false;
    }
    if (!form.platformSetup) {
      message.error("Please select a setup.");
      return false;
    }
    if (!form.tier) {
      message.error("Please select a tier.");
      return false;
    }
    if (!isEdit && !imageFile && !form.bannerUrl) {
      message.error("Please upload a banner image.");
      return false;
    }
    if (form.redirectUrl && !isValidUrl(form.redirectUrl)) {
      message.error("Please enter a valid redirect URL.");
      return false;
    }
    if (checkComboConflict()) {
      message.error(COMBO_CONFLICT_MESSAGE);
      return false;
    }
    return true;
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return form.bannerUrl;
    const formData = new FormData();
    formData.append("image", imageFile);
    const imgResp = await uploadImage(formData);
    if (imgResp?.code !== 0 || !imgResp?.result?.files?.[0]) {
      throw new Error(imgResp?.message || "Image upload failed.");
    }
    return imgResp.result.files[0];
  };

  const submitCreate = async () => {
    const bannerUrl = await uploadImageIfNeeded();
    const body = {
      bannerUrl,
      platform: form.platform,
      platformSetup: form.platformSetup,
      tier: form.tier,
      isActive: Boolean(form.isActive),
    };
    if (form.redirectUrl) body.redirectUrl = form.redirectUrl;

    const resp = await createPlatformBanner(body);
    const ok = resp?.status === 201 || resp?.code === 201 || resp?.code === 0;
    if (!ok) {
      throw new Error(resp?.message || "Failed to create banner.");
    }
    message.success(resp?.message || "Banner added successfully.");
    return resp?.data || resp?.result;
  };

  const submitEdit = async () => {
    const init = initialRef.current;
    const body = {};

    if (imageFile) {
      body.bannerUrl = await uploadImageIfNeeded();
    }

    if ((form.redirectUrl || "") !== (init.redirectUrl || "")) {
      // Per backend doc: send `null` to clear, omit to leave untouched.
      body.redirectUrl = form.redirectUrl ? form.redirectUrl : null;
    }

    if (form.isActive !== init.isActive) {
      body.isActive = Boolean(form.isActive);
    }

    if (form.platform !== init.platform) {
      body.platform = form.platform;
    }
    if (form.platformSetup !== init.platformSetup) {
      body.platformSetup = form.platformSetup;
    }
    if (form.tier !== init.tier) {
      body.tier = form.tier;
    }

    if (Object.keys(body).length === 0) {
      onClose();
      return null;
    }

    const resp = await updatePlatformBanner(body, banner._id);
    const ok = resp?.status === 200 || resp?.code === 200 || resp?.code === 0;
    if (!ok) {
      throw new Error(resp?.message || "Failed to update banner.");
    }
    message.success(resp?.message || "Banner updated successfully.");
    return resp?.data || resp?.result;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      const saved = isEdit ? await submitEdit() : await submitCreate();
      if (saved !== null && onSaved) onSaved(saved);
    } catch (err) {
      const status = err?.status;
      if (status === 409) {
        message.error(COMBO_CONFLICT_MESSAGE);
      } else {
        message.error(err?.message || "Something went wrong.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestClose = () => {
    if (submitting) return;
    if (isDirty) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  };

  const tierDisabled = !form.platform || !form.platformSetup;
  const setupDisabled = !form.platform;

  const modalTitle = isEdit ? "Edit Banner" : "Add a Banner";

  let tierPlaceholder = "Select Tier";
  if (tierDisabled) {
    tierPlaceholder = form.platform
      ? "Select a setup first"
      : "Select a domain first";
  }
  const tierHint = form.platform
    ? "Pick a setup above to see available tiers."
    : "Pick a domain above to see available tiers.";

  return (
    <>
      <Modal
        open={open}
        onCancel={handleRequestClose}
        title={modalTitle}
        footer={null}
        destroyOnClose
        maskClosable={!isDirty}
        width={520}
        className="bannerFormModal"
      >
        <div className="bannerFormBody">
          <div className="bannerFormField">
            <div className="bannerFormLabel">Select Domain</div>
            <Select
              placeholder="Select Domain"
              value={form.platform}
              onChange={handlePlatformChange}
              disabled={submitting}
              style={{ width: "100%" }}
            >
              {platformOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <div className={`bannerFormField${setupDisabled ? " isLocked" : ""}`}>
            <div className="bannerFormLabel">Select Setup</div>
            <Select
              placeholder={
                setupDisabled ? "Select a domain first" : "Select Setup"
              }
              value={form.platformSetup}
              onChange={handleSetupChange}
              disabled={submitting || setupDisabled}
              style={{ width: "100%" }}
            >
              {setupOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            {setupDisabled ? (
              <div className="bannerFormHint">
                Pick a domain above to see available setups.
              </div>
            ) : null}
          </div>

          <div className={`bannerFormField${tierDisabled ? " isLocked" : ""}`}>
            <div className="bannerFormLabel">Select Tier</div>
            <Select
              placeholder={tierPlaceholder}
              value={form.tier}
              onChange={handleTierChange}
              disabled={submitting || tierDisabled}
              style={{ width: "100%" }}
            >
              {tierOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            {tierDisabled ? (
              <div className="bannerFormHint">{tierHint}</div>
            ) : null}
          </div>

          <div className="bannerFormField">
            <div className="bannerFormLabel">Upload Banner Image</div>
            <Upload
              className="bannerImageUpload"
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
              accept="image/*"
              disabled={submitting}
            >
              {imagePreview ? (
                <div className="bannerImagePreviewWrapper">
                  <img
                    src={imagePreview}
                    alt="banner preview"
                    className="bannerImagePreview"
                  />
                  <div className="bannerImageHoverOverlay">
                    <span>Change Image</span>
                  </div>
                </div>
              ) : (
                <div className="bannerImageDropzone">
                  <CloudUploadOutlined className="bannerImageDropzoneIcon" />
                  <Typography.Text strong>
                    Upload banner thumbnail
                  </Typography.Text>
                  <Typography.Text type="secondary" className="hintText">
                    Images should be horizontal, at least{" "}
                    {BANNER_IMAGE_HINT.minWidth}×{BANNER_IMAGE_HINT.minHeight}{" "}
                    px. The maximum image size should be{" "}
                    {BANNER_IMAGE_HINT.maxSizeLabel}.
                  </Typography.Text>
                </div>
              )}
            </Upload>
          </div>

          <div className="bannerFormField">
            <div className="bannerFormLabel">Redirect URL</div>
            <Input
              placeholder="http://example.com/"
              value={form.redirectUrl}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, redirectUrl: e.target.value }))
              }
              disabled={submitting}
            />
          </div>

          <div className="bannerFormField bannerActiveRow">
            <div className="bannerActiveCopy">
              <Typography.Text strong>Activate Banner</Typography.Text>
              <Typography.Text type="secondary" className="hintText">
                Make this banner live now. You can change this later from
                listings.
              </Typography.Text>
            </div>
            <Switch
              checked={Boolean(form.isActive)}
              onChange={(checked) =>
                setForm((prev) => ({ ...prev, isActive: checked }))
              }
              disabled={submitting}
            />
          </div>

          <Button
            block
            className="siteBtn accentBtnV1 bannerSubmitBtn"
            loading={submitting}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {isEdit ? "Save" : "Create Banner"}
          </Button>
        </div>
      </Modal>

      <ConfirmationModal
        open={discardOpen}
        title="Discard changes?"
        message="Your progress hasn't been saved. If you leave now, all changes will be lost."
        cancelButtonText="Continue editing"
        confirmButtonText="Discard"
        confirmButtonVariant="siteBtnDanger"
        onCancel={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
      />
    </>
  );
};

const bannerShape = PropTypes.shape({
  _id: PropTypes.string,
  bannerUrl: PropTypes.string,
  redirectUrl: PropTypes.string,
  platform: PropTypes.string,
  platformSetup: PropTypes.string,
  tier: PropTypes.string,
  isActive: PropTypes.bool,
});

BannerFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  banner: bannerShape,
  banners: PropTypes.arrayOf(bannerShape),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

BannerFormModal.defaultProps = {
  banner: null,
  banners: [],
  onSaved: undefined,
};

export default BannerFormModal;
